import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

// TODO: (nachiket) When migrating to use shared libs instead of forked ones, replace these usages with the correct VDS tokens.
import { colors } from '@verantos-dev/shared-ui-components/tokens';

import {
  ACCURACY_JOB_STATUS,
  ACCURACY_JOB_TERMINAL_STATUSES,
} from '../../../generate-accuracy/constants';
import {
  formatAccuracyStageLabel,
  formatEtaSeconds,
  formatTimeAgo,
} from '../../emitters/formatAccuracyJobCell';
import { formatAccuracyRunConfig } from '../../emitters/formatAccuracyRunConfig';
import type { AccuracyRunActionType, AccuracyRunView } from '../../hook/useAccuracyRunHistory';

type StatusVariant = 'in-progress' | 'queued' | 'completed' | 'failed';

const resolveStatusVariant = (status: string): StatusVariant => {
  if (status === ACCURACY_JOB_STATUS.PENDING) return 'queued';
  if (!ACCURACY_JOB_TERMINAL_STATUSES.has(status)) return 'in-progress';
  if (status === ACCURACY_JOB_STATUS.COMPLETED) return 'completed';
  return 'failed';
};

export interface AccuracyRunRowStatusChip {
  label: string;
  dotColor: string | null;
  fg: string;
  bg: string;
}

export interface UseAccuracyRunRowResult {
  statusChip: AccuracyRunRowStatusChip;
  jobReference: string;
  /** Failure reason for a failed run, otherwise the run's config summary; `null` when neither is known. */
  secondaryLine: string | null;
  etaLabel: string | null;
  timestampLabel: string | null;
  actionType: AccuracyRunActionType;
}

/**
 * Derives the display model for one accuracy run row — status chip (label +
 * colours), config / error summary line, ETA and relative timestamp — from the
 * run's status and progress. No queries and no navigation: `AccuracyRunsModal`
 * owns both.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const useAccuracyRunRow = (view: AccuracyRunView): UseAccuracyRunRowResult => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { run, actionType } = view;

  const variant = resolveStatusVariant(run.status);
  const percent = run.progress?.percent ?? 0;
  const stageLabel = formatAccuracyStageLabel(
    run.status,
    run.progress?.currentStep,
    formatMessage({ id: 'adminProjects.accuracyRunsModal.run.inProgressFallback' })
  );

  const statusChipByVariant: Record<StatusVariant, AccuracyRunRowStatusChip> = {
    'in-progress': {
      label: formatMessage(
        { id: 'adminProjects.accuracyRunsModal.run.inProgress' },
        { stage: stageLabel, percent }
      ),
      dotColor: theme.palette.primary.main,
      fg: theme.palette.primary.main,
      bg: colors['primary-100'],
    },
    queued: {
      label:
        run.queuePosition != null
          ? formatMessage(
              { id: 'adminProjects.accuracyRunsModal.run.queuedWithPosition' },
              { position: run.queuePosition }
            )
          : formatMessage({ id: 'adminProjects.accuracyRunsModal.run.queued' }),
      dotColor: theme.palette.grey[500],
      fg: theme.palette.text.secondary,
      bg: theme.palette.grey[100],
    },
    completed: {
      label: formatMessage({ id: 'adminProjects.accuracyRunsModal.run.completed' }),
      dotColor: theme.palette.success.main,
      fg: theme.palette.success.dark,
      bg: colors['success-100'],
    },
    failed: {
      label: formatMessage({ id: 'adminProjects.accuracyRunsModal.run.failed' }),
      dotColor: theme.palette.error.main,
      fg: theme.palette.error.dark,
      bg: colors['error-100'],
    },
  };

  const isActive = !ACCURACY_JOB_TERMINAL_STATUSES.has(run.status);
  const timestampSource = isActive ? run.submittedAt : run.finishedAt;
  const timestampLabel = timestampSource
    ? formatMessage(
        {
          id: isActive
            ? 'adminProjects.accuracyRunsModal.run.submittedAgo'
            : 'adminProjects.accuracyRunsModal.run.finishedAgo',
        },
        { time: formatTimeAgo(timestampSource, formatMessage) }
      )
    : null;

  const etaLabel =
    variant === 'in-progress' && run.progress?.etaSeconds != null
      ? formatEtaSeconds(run.progress.etaSeconds, formatMessage)
      : null;

  const secondaryLine =
    run.status === ACCURACY_JOB_STATUS.FAILED && run.errorMessage
      ? run.errorMessage
      : formatAccuracyRunConfig(run);

  return {
    statusChip: statusChipByVariant[variant],
    jobReference: run.jobReference,
    secondaryLine: secondaryLine || null,
    etaLabel,
    timestampLabel,
    actionType,
  };
};
